import type { ComponentProps } from 'react';

import { cn, getColoredCellStyle } from '../../lib/utils';
import { Icon } from '../../primitives';

type RatingToggleProps = Omit<ComponentProps<'button'>, 'color'> & {
  color: string;
  selected?: boolean;
  readOnly?: boolean;
};

function RatingToggle({
  color,
  selected = false,
  readOnly = false,
  disabled,
  className,
  onClick,
  children,
  ...props
}: RatingToggleProps) {
  const isDisabled = readOnly || !!disabled;
  const isInteractive = !!onClick && !isDisabled;

  return (
    <button
      data-slot={'rating-toggle'}
      type={'button'}
      aria-pressed={selected}
      disabled={isDisabled}
      className={cn(
        'relative flex flex-col items-center justify-center h-[46px] rounded-lg',
        'border border-solid border-transparent',
        'transition-all duration-200 ease-out',
        selected && 'border-primary-active',
        isInteractive && 'cursor-pointer hover:opacity-90',
        readOnly && !selected && 'opacity-60',
        className
      )}
      style={getColoredCellStyle(color)}
      onClick={isDisabled ? undefined : onClick}
      {...props}
    >
      {children}
      {selected && <RatingToggleCheck />}
    </button>
  );
}

function RatingToggleLabel({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-label'}
      className={cn('text-sm font-semibold whitespace-nowrap', className)}
      {...props}
    />
  );
}

function RatingToggleValue({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-value'}
      className={cn('text-sm font-semibold', className)}
      {...props}
    />
  );
}

function RatingToggleCheck({
  className,
  ...props
}: ComponentProps<'span'>) {
  return (
    <span
      data-slot={'rating-toggle-check'}
      className={cn(
        'absolute top-1 right-1 flex items-center justify-center size-5 rounded-full bg-primary-active',
        className
      )}
      {...props}
    >
      <Icon name={'check'} size={'xs'} />
    </span>
  );
}

RatingToggle.Label = RatingToggleLabel;
RatingToggle.Value = RatingToggleValue;
RatingToggle.Check = RatingToggleCheck;

export { RatingToggle };
export type { RatingToggleProps };
